<template>
  <div
      class="input-group"
      :class="{'disable': isDisable, 'error': hasValidationError(), 'changeableTextElement': changeableTextElement, [inputGroupClass]: inputGroupClass}"
  >
    <div class="icon" v-if="withIcon && iconPosition === 'left'">
      <LucideIcon :icon="icon"/>
    </div>
    <input
        :id="name"
        :name="name"
        :type="typeOfInput"
        :placeholder="placeholder"
        :disabled="isDisable"
        v-model="inputModel"
        :autocomplete="autocomplete"
        class="form-control"
        :class="{'input-passive': isPassive}"
        @input="$emit('input')"
        @keydown="$emit('keydown', $event)"
        @blur="$emit('blur')"
        :data-ignore-keyboard-shortcut-events="ignoreKeyboardShortCutEvents"
    />
    <template v-if="!!callbackButton">
      <ButtonElement
          @clickOnButton="callbackButton.callback"
          size="sm"
          class-type="button"
          class="btn--ms btn--neutral-secondary btn--xs"
          :class="callbackButton.class"
      >{{ callbackButton.label }}
      </ButtonElement>
    </template>

    <div class="icon" v-if="withIcon && iconPosition === 'right'">
      <LucideIcon :icon="icon"/>
    </div>

    <div v-if="isLocked" class="icon-side">
      <LucideIcon icon="Lock"/>
    </div>
  </div>

  <div v-if="hasValidationError()" class="validation-error">
    <LucideIcon icon="Info"/>
    <span v-for="(error, index) of validator.$errors" :key="index">{{ error.$message }}</span>
  </div>
</template>

<script lang="ts">
import {defineComponent, type PropType} from 'vue'
import {AbstractInputComponent} from '@shared/components/abstracts'
import ButtonElement from '@shared/components/elements/button/button.element.vue'
import {CommonNs} from "@shared/types";

export default defineComponent({
  name: 'InputElement',
  extends: AbstractInputComponent,
  components: {
    ButtonElement
  },
  emits: ['input', 'keydown', 'update:modelValue', 'blur'],
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    placeholder: {
      type: String
    },
    withIcon: {
      type: Boolean,
      default: false
    },
    iconType: {
      type: String,
      default: 'fal'
    },
    icon: {
      type: String,
      default: 'rocket'
    },
    callbackButton: {
      type: Object as PropType<CommonNs.CallbackButton>
    },
    isPassive: {
      type: Boolean,
      default: false
    },
    isLocked: {
      type: Boolean,
      default: false
    },
    validator: {
      type: Object,
      default: null
    },
    changeableTextElement: {
      type: Boolean,
      default: null
    },
    iconPosition: {
      type: String,
      default: 'left',
      enum: ['right', 'left']
    },
    autocomplete: {
      type: String,
      default: 'off',
      enum: ['on', 'off']
    },
    ignoreKeyboardShortCutEvents: {
      type: Boolean,
      default: false,
    },
    inputGroupClass: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      typeOfInput: this.type
    }
  },
  computed: {
    inputModel: {
      get(): string {
        return this.modelValue
      },
      set(value: string): void {
        this.$emit('update:modelValue', value)
      }
    }
  },
  methods: {
    hasValidationError(): boolean {
      return this.validator && this.validator.$errors.length
    }
  },
  mounted(): void {
  },
  inheritAttrs: false
})
</script>

<style lang="scss" scoped>
.input-group {
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: $br-sm;
  background-color: var(--form-bg);
  border: 1px solid var(--form-border);
  box-shadow: none !important;

  &:hover,
  &:focus,
  &:active {
    background-color: var(--form-bg-hover);
    border: 1px solid var(--form-border-hover);
    box-shadow: var(--form-shadow-outline) !important;
  }

  &:has(input:focus) {
    background-color: var(--form-bg-focus);
    border: 1px solid var(--form-border-focus);
    box-shadow: var(--form-shadow-outline) !important;

    .tag {
      background-color: var(--tag-form-bg-hover);
      border: 1px solid var(--tag-form-border-hover);
    }
  }


  &.disable {
    background-color: var(--form-bg);
    border: 1px solid var(--form-border);

    input {
      color: var(--text-neutral);
      box-shadow: 0 !important;
    }

    &:hover {
      background-color: var(--form-bg-hover);
      border: 1px solid var(--form-border-hover);
      box-shadow: var(--form-shadow-outline) !important;
    }
  }

  .btn {
    border-radius: $br-sm !important;
    margin-right: var(--space-12) !important;
  }

  &.input-form-group {
    box-shadow: none;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-bottom: 0;
    cursor: pointer;
    background-color: var(--light-300);
    border: 2px solid var(--light-300);

    &:hover,
    &:focus,
    &:active {
      background-color: var(--light-100);
      border: 2px solid var(--light-100);
      box-shadow: var(--shadow-lg) !important;
      outline: 0 !important;
    }

    &.disable {
      &:hover {
        background: var(--light-300);
        border: 2px solid var(--light-300);
        box-shadow: none !important;
      }
    }
  }

  &.error {
    box-shadow: 0px 0px 4px 0px var(--red-100) !important;
    background-color: var(--light-100) !important;
    border-color: var(--light-100) !important;
  }

  input,
  .input-group-text {
    background: transparent;
    border: 0;
  }

  .input-group-text {
    color: var(--text-neutral);
  }

  input {
    height: 36px;
    border-radius: $br-sm;
    font-size: var(--fs-14);
    cursor: pointer;
    color: var(--text-dark);

    &::-webkit-input-placeholder {
      /* Edge */
      color: var(--text-neutral);
    }

    &:-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: var(--text-neutral);
    }

    &::placeholder {
      color: var(--text-neutral);
    }
  }

  &.changeableTextElement {
    border: 0;
    width: 100%;
    padding: 5px 5px;
    font-size: var(--fs-32);
    font-weight: 600;
    letter-spacing: inherit;
    border-radius: $br-sm;
    margin: var(--space-0);
    min-height: 55px;
    color: var(--text-dark);
    outline: 0;
    background-color: transparent;
    border: 2px solid transparent;
    word-break: break-word;

    input {
      font-size: var(--fs-24);
      font-weight: 600;
      padding: 0;

      &:hover {
        .changeableTextElement {
          background-color: var(--light-300);
          border: 2px solid var(--light-300);
        }
      }

      &:focus,
      &:active {
        .changeableTextElement {
          border-radius: $br-sm !important;
          background-color: var(--light-100) !important;
          border: 2px solid var(--light-100) !important;
          box-shadow: var(--shadow-lg) !important;
          outline: 0;
        }
      }
    }
  }

  .icon-side {
    align-items: center;
    height: 100%;
    padding: 0 15px 0 0px;
    color: var(--text-neutral);
  }

  .icon {
    width: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    color: var(--text-neutral);
  }
}


.changeable-text-element {
  margin-bottom: 0 !important;
  border: 0 solid transparent;
  box-shadow: 0 0 0;

  & > input {
    font-size: 1.125rem;
    padding: 0;
    background: transparent;

    &.input-passive {
      color: var(--text-neutral);
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0;
    }

    &::-webkit-input-placeholder {
      /* Edge */
      color: var(--text-neutral);
    }

    &:-ms-input-placeholder {
      /* Internet Explorer 10-11 */
      color: var(--text-neutral);
    }

    &::placeholder {
      color: var(--text-neutral);
    }
  }
}

.input-group-br-left-0 {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}

.input-group-br-right-0 {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-left: 0;
}
</style>
